import ClearOutlined from '@ant-design/icons/ClearOutlined';
import React, { useMemo, useState } from 'react';

import type {
  FilterBarProps,
  FilterConfig,
} from '../DataTable/DataTable.types';
import { ClearAllButton, FilterContainer } from './FilterBar.styles';
import { FilterDropdown } from './FilterDropdown';
import { FilterInput } from './FilterInput';

const hasFilterValue = (filter: FilterConfig): boolean => {
  switch (filter.type) {
    case 'select':
    case 'search':
      return Boolean(filter.value);

    case 'dateRange':
    case 'number':
      return Boolean(filter.value && filter.value[0] && filter.value[1]);

    case 'boolean':
      return filter.value !== undefined;
  }
};

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onClearAll,
  maxVisible = 4,
}) => {
  // Track which filters are visible
  const [visibleFilterKeys, setVisibleFilterKeys] = useState<Set<string>>(
    () => {
      // Initially show filters that have values or are within maxVisible
      const initialVisible = new Set<string>();
      let count = 0;
      filters.forEach((filter) => {
        if (hasFilterValue(filter) || count < maxVisible) {
          initialVisible.add(filter.key);
          count++;
        }
      });
      return initialVisible;
    },
  );

  const visibleFilters = useMemo(() => {
    return filters.filter((f) => visibleFilterKeys.has(f.key));
  }, [filters, visibleFilterKeys]);

  const hiddenFilters = useMemo(() => {
    return filters.filter((f) => !visibleFilterKeys.has(f.key));
  }, [filters, visibleFilterKeys]);

  const activeFilters = useMemo(() => {
    return filters.filter(hasFilterValue);
  }, [filters]);

  const handleAddFilter = (filter: FilterConfig) => {
    setVisibleFilterKeys((prev) => new Set(prev).add(filter.key));
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <FilterContainer>
      {/* Render visible filter inputs */}
      {visibleFilters.map((filter) => (
        <FilterInput key={filter.key} filter={filter} />
      ))}

      {/* Add filter dropdown for hidden filters */}
      <FilterDropdown
        availableFilters={hiddenFilters}
        onFilterSelect={handleAddFilter}
      />

      {/* Clear all button */}
      {activeFilters.length > 0 && onClearAll && (
        <ClearAllButton
          icon={<ClearOutlined />}
          size="small"
          onClick={onClearAll}
        >
          Clear All
        </ClearAllButton>
      )}
    </FilterContainer>
  );
};
